"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, FileCheck2, SlidersHorizontal as Sliders, Layers, ArrowRight, ShieldCheck, Activity, Microscope, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
interface RoadmapStage {
  step: string;
  title: string;
  description: string;
  keyOutputs: string;
  timeline: string;
  iconName: "microscope" | "sliders" | "layers" | "filecheck";
}
const stages: RoadmapStage[] = [{
  step: "Stage 01",
  title: "Feasibility & Optical Path Assessment",
  description: "Comprehensive evaluation of sample optical properties, transmission geometry, probe selection, and spectral signal-to-noise ratio in operational environments.",
  keyOutputs: "Optical window specification, pathlength optimisation report, initial feasibility dataset",
  timeline: "Weeks 1–3",
  iconName: "microscope"
}, {
  step: "Stage 02",
  title: "Calibration Model Development",
  description: "Chemometric modeling using PLS/PCR algorithms, multivariate spectral preprocessing, and secondary reference method correlation for active pharmaceutical ingredients.",
  keyOutputs: "Raw chemometric calibration model, spectral outlier bounds, preprocessing matrix",
  timeline: "Weeks 4–7",
  iconName: "sliders"
}, {
  step: "Stage 03",
  title: "Model Validation & Robustness Testing",
  description: "Rigorous blind testing against orthogonal validation sets, temperature drift compensation, batch-to-batch variation assessments, and repeatability trials.",
  keyOutputs: "ICH Q2(R2) analytical validation report, standard error of prediction (SEP) benchmark",
  timeline: "Weeks 8–10",
  iconName: "layers"
}, {
  step: "Stage 04",
  title: "System Qualification & Automation Integration",
  description: "End-to-end integration into distributed control systems (DCS/SCADA), automated spectral acquisition routines, qualification documentation (IQ/OQ/PQ), and data integrity alignment.",
  keyOutputs: "Execution-ready IQ/OQ protocols, OPC UA communication bridge, full system handover dossier",
  timeline: "Weeks 11–14",
  iconName: "filecheck"
}];
export default function Roadmap() {
  const shouldReduceMotion = useReducedMotion();
  const getStageIcon = (name: RoadmapStage["iconName"]) => {
    switch (name) {
      case "microscope":
        return <Microscope className="size-5 text-primary" />;
      case "sliders":
        return <Sliders className="size-5 text-primary" />;
      case "layers":
        return <Layers className="size-5 text-primary" />;
      case "filecheck":
        return <FileCheck2 className="size-5 text-primary" />;
      default:
        return <Activity className="size-5 text-primary" />;
    }
  };
  return <section id="roadmapprocess" data-nav="light" className="relative bg-background py-20 lg:py-28 text-foreground overflow-hidden">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 lg:mb-16">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/5">
            <Sparkles className="size-3 mr-1 text-primary" />
            Methodology & Implementation
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Structured 4-Stage Analytical Process
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            From initial spectral feasibility testing to qualified in-line automation, our end-to-end lifecycle follows rigorous regulatory standards.
          </p>
        </div>

        {/* 8 / 4 Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Left Column: 8 Cols - Roadmap Stages */}
          <div className="lg:col-span-8 space-y-6">
            {stages.map((stage, i) => <motion.div key={stage.step} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.45,
            delay: i * 0.08
          }}>
                <Card className="relative overflow-hidden border border-border bg-card shadow-sm hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                  <div className="absolute top-0 left-0 bottom-0 w-1.5 bg-primary" />
                  
                  <CardHeader className="pb-3 pt-6 px-6 sm:px-8">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center size-10 rounded-lg bg-primary/10 border border-primary/20">
                          {getStageIcon(stage.iconName)}
                        </div>
                        <span className="text-xs font-semibold tracking-wider uppercase text-primary">
                          {stage.step}
                        </span>
                      </div>
                      <Badge variant="secondary" className="text-xs font-mono">
                        {stage.timeline}
                      </Badge>
                    </div>

                    <CardTitle className="text-xl sm:text-2xl font-bold font-default text-card-foreground">
                      {stage.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-6 sm:px-8 pb-6 text-sm sm:text-base space-y-4">
                    <p className="text-muted-foreground leading-relaxed">
                      {stage.description}
                    </p>

                    <div className="pt-3 border-t border-border flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                        Key Deliverables:
                      </span>
                      <span className="text-xs sm:text-sm text-muted-foreground">
                        {stage.keyOutputs}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>

          {/* Right Column: 4 Cols - Supporting Context & Verification */}
          <div className="lg:col-span-4 space-y-6">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="sticky top-28 space-y-6">
              {/* Supporting Overview Card */}
              <div className="rounded-xl border border-border bg-secondary/50 p-6 sm:p-7">
                <div className="flex items-center gap-2 text-primary font-semibold text-sm mb-3">
                  <ShieldCheck className="size-5 text-primary" />
                  <span>Quality By Design Framework</span>
                </div>
                <h3 className="text-xl font-bold font-default text-foreground mb-3">
                  Lifecycle Validation & Regulatory Adherence
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                  Every stage is documented according to established industry guidelines to provide comprehensive regulatory documentation support and data integrity across laboratory and manufacturing sites.
                </p>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-foreground">
                      Traceable chemometric model version control
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-foreground">
                      On-site process analytical tech (PAT) integration
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span className="text-xs sm:text-sm text-foreground">
                      Independent IQ/OQ/PQ protocol verification
                    </span>
                  </div>
                </div>

                <div className="pt-5 border-t border-border">
                  <Button asChild size="lg" className="w-full justify-between">
                    <Link to="/ContactPage">
                      <span>Schedule Assessment</span>
                      <ArrowRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Consultation Teaser Card */}
              <div className="rounded-xl border border-border bg-card p-6 shadow-xs">
                <h4 className="text-base font-bold font-default text-foreground mb-2">
                  Need a custom protocol?
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground mb-4">
                  Speak directly with our chemometricians and qualification engineers to plan your timeline.
                </p>
                <Button asChild variant="outline" size="sm" className="w-full">
                  <Link to="/ServicesPage">
                    Explore Support Services
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}